/**
 * ResourcesPage - Phase 5 Enhanced Resources Management
 * Advanced resource management with integrated Phase 4 components
 * 
 * @description Professional resource management interface combining:
 * - Advanced resource table with filtering, sorting, and bulk operations
 * - Real-time resource status monitoring
 * - Cost analysis and optimization recommendations
 * - Multi-cloud resource management with provider-specific features
 * - Compliance and security resource scanning
 */

import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";

// Phase 3: Global + Local Hybrid - Import global provider state and filtering utilities
import { useCloudProviderStore } from '@/stores/cloudProviderStore';
import { filterByProviderHierarchy, getLocalProviderFilterOptions, getFilterSummary } from '@/utils/providerFiltering';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Plus,
  Download,
  Filter,
  Search,
  MoreVertical,
  Power,
  RefreshCw,
  Trash2,
  Settings,
  Activity,
  DollarSign,
  Shield,
  Server,
  Database,
  HardDrive,
  Network,
  Cloud
} from "lucide-react";

// Enhanced resource type for Phase 5
interface EnhancedResource {
  id: string;
  name: string;
  type: 'compute' | 'storage' | 'database' | 'network';
  provider: 'aws' | 'gcp' | 'azure';
  status: 'running' | 'stopped' | 'error' | 'pending';
  health: 'healthy' | 'warning' | 'critical';
  region: string;
  cost: number;
  instance_type?: string;
  created_date: string;
  tags: Record<string, string>;
  compliance_status: 'compliant' | 'non-compliant' | 'pending';
  last_backup?: string;
  security_score: number;
}

// Mock data for Phase 5 demonstration
const mockResources: EnhancedResource[] = [
  {
    id: 'aws-ec2-001',
    name: 'web-server-prod-01',
    type: 'compute',
    provider: 'aws',
    status: 'running',
    health: 'healthy',
    region: 'us-east-1',
    cost: 89.50,
    instance_type: 't3.medium',
    created_date: '2024-01-15T10:30:00Z',
    tags: { environment: 'production', team: 'frontend' },
    compliance_status: 'compliant',
    last_backup: '2024-01-20T02:00:00Z',
    security_score: 85
  },
  {
    id: 'aws-rds-001',
    name: 'user-database-main',
    type: 'database',
    provider: 'aws',
    status: 'running',
    health: 'healthy',
    region: 'us-east-1',
    cost: 156.80,
    instance_type: 'db.t3.small',
    created_date: '2024-01-10T14:20:00Z',
    tags: { environment: 'production', team: 'backend' },
    compliance_status: 'compliant',
    last_backup: '2024-01-20T01:00:00Z',
    security_score: 92
  },
  {
    id: 'gcp-vm-001',
    name: 'analytics-worker',
    type: 'compute',
    provider: 'gcp',
    status: 'running',
    health: 'warning',
    region: 'us-central1',
    cost: 124.30,
    instance_type: 'n2-standard-2',
    created_date: '2024-01-12T09:15:00Z',
    tags: { environment: 'production', team: 'analytics' },
    compliance_status: 'pending',
    last_backup: '2024-01-19T03:00:00Z',
    security_score: 78
  },
  {
    id: 'azure-vm-001',
    name: 'backup-server',
    type: 'compute',
    provider: 'azure',
    status: 'stopped',
    health: 'healthy',
    region: 'eastus',
    cost: 67.20,
    instance_type: 'Standard_B2s',
    created_date: '2024-01-08T16:45:00Z',
    tags: { environment: 'staging', team: 'devops' },
    compliance_status: 'compliant',
    last_backup: '2024-01-18T04:00:00Z',
    security_score: 88
  },
  {
    id: 'aws-s3-001',
    name: 'media-storage-bucket',
    type: 'storage',
    provider: 'aws',
    status: 'running',
    health: 'healthy',
    region: 'us-west-2',
    cost: 23.40,
    created_date: '2024-01-05T11:30:00Z',
    tags: { environment: 'production', team: 'content' },
    compliance_status: 'compliant',
    security_score: 90
  }
];

const typeIcons = {
  compute: Server,
  storage: HardDrive,
  database: Database,
  network: Network
};

const statusColors = {
  running: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  stopped: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  error: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  pending: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300'
};

const healthColors = {
  healthy: 'bg-green-500',
  warning: 'bg-yellow-500',
  critical: 'bg-red-500'
};

const providerColors = {
  aws: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300',
  gcp: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  azure: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900 dark:text-cyan-300'
};

export default function ResourcesPage() {
  // Phase 3: Global + Local Hybrid - Get global provider state
  const globalProvider = useCloudProviderStore((state) => state.provider);
  
  const [resources] = useState<EnhancedResource[]>(mockResources);
  const [filteredResources, setFilteredResources] = useState<EnhancedResource[]>(mockResources);
  const [selectedResources, setSelectedResources] = useState<string[]>([]);
  
  // Filter states - providerFilter now works within global provider scope
  const [searchTerm, setSearchTerm] = useState("");
  const [providerFilter, setProviderFilter] = useState<string>("all"); // "all" = all within global provider
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [healthFilter, setHealthFilter] = useState<string>("all");
  
  // Apply filters whenever filter state changes - Phase 3: Global + Local Hybrid
  useEffect(() => {
    let filtered = [...resources];
    
    // Step 1: Apply global provider filter (PRIMARY - from navbar)
    filtered = filterByProviderHierarchy(filtered, globalProvider, providerFilter);
    
    // Step 2: Apply remaining filters within global provider scope (SECONDARY)
    
    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(resource =>
        resource.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.region.toLowerCase().includes(searchTerm.toLowerCase()) ||
        resource.instance_type?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    // Type filter
    if (typeFilter !== "all") {
      filtered = filtered.filter(resource => resource.type === typeFilter);
    }
    
    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(resource => resource.status === statusFilter);
    }
    
    // Health filter
    if (healthFilter !== "all") {
      filtered = filtered.filter(resource => resource.health === healthFilter);
    }
    
    setFilteredResources(filtered);
  }, [resources, globalProvider, searchTerm, providerFilter, typeFilter, statusFilter, healthFilter]);

  const handleSelectResource = useCallback((resourceId: string, selected: boolean) => {
    setSelectedResources(prev => 
      selected 
        ? [...prev, resourceId]
        : prev.filter(id => id !== resourceId)
    );
  }, []);

  const handleSelectAll = useCallback((checked: boolean) => {
    setSelectedResources(checked ? filteredResources.map(r => r.id) : []);
  }, [filteredResources]);

  const handleBulkAction = async (action: string) => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    console.log(`Performing ${action} on resources:`, selectedResources);
    
    // Here you would make actual API calls
    // await performBulkAction(action, selectedResources);
    
    setSelectedResources([]);
  };

  const resetFilters = () => {
    setSearchTerm("");
    setProviderFilter("all");
    setTypeFilter("all");
    setStatusFilter("all");
    setHealthFilter("all");
  };

  const getResourceStats = () => {
    const total = filteredResources.length;
    const running = filteredResources.filter(r => r.status === 'running').length;
    const healthy = filteredResources.filter(r => r.health === 'healthy').length;
    const totalCost = filteredResources.reduce((sum, r) => sum + r.cost, 0);
    
    return { total, running, healthy, totalCost };
  };

  const stats = getResourceStats();

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div>
        <h1 className="text-3xl font-bold text-foreground mb-2">Resources</h1>
        <p className="text-muted-foreground">
          Manage and monitor your multi-cloud infrastructure resources
        </p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Total Resources</p>
                <p className="text-2xl font-bold text-foreground">{stats.total}</p>
              </div>
              <Cloud className="w-8 h-8 text-muted-foreground" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Running</p>
                <p className="text-2xl font-bold text-green-600">{stats.running}</p>
              </div>
              <Activity className="w-8 h-8 text-green-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Healthy</p>
                <p className="text-2xl font-bold text-blue-600">{stats.healthy}</p>
              </div>
              <Shield className="w-8 h-8 text-blue-600" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Monthly Cost</p>
                <p className="text-2xl font-bold text-orange-600">${stats.totalCost.toFixed(2)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-orange-600" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Resource Management</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
              <Button size="sm">
                <Plus className="w-4 h-4 mr-2" />
                Add Resource
              </Button>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filter Summary */}
          <div className="text-sm text-muted-foreground">
            {getFilterSummary(globalProvider, providerFilter)}
          </div>
          
          {/* Filter Controls */}
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search resources..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <Select value={providerFilter} onValueChange={setProviderFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Provider" />
              </SelectTrigger>
              <SelectContent>
                {getLocalProviderFilterOptions(globalProvider).map(option => (
                  <SelectItem key={option.value} value={option.value}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="compute">Compute</SelectItem>
                <SelectItem value="storage">Storage</SelectItem>
                <SelectItem value="database">Database</SelectItem>
                <SelectItem value="network">Network</SelectItem>
              </SelectContent>
            </Select>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="running">Running</SelectItem>
                <SelectItem value="stopped">Stopped</SelectItem>
                <SelectItem value="error">Error</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
              </SelectContent>
            </Select>
            
            <Button variant="outline" onClick={resetFilters} size="sm">
              <Filter className="w-4 h-4 mr-2" />
              Reset
            </Button>
          </div>

          {/* Bulk Actions */}
          {selectedResources.length > 0 && (
            <div className="flex items-center gap-2 p-4 bg-muted rounded-lg">
              <span className="text-sm font-medium">
                {selectedResources.length} resource(s) selected
              </span>
              <div className="flex gap-2 ml-auto">
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Power className="w-4 h-4 mr-2" />
                      Start
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Start Resources</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to start {selectedResources.length} selected resource(s)?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleBulkAction('start')}>
                        Start Resources
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="outline" size="sm">
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Restart
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Restart Resources</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to restart {selectedResources.length} selected resource(s)?
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction onClick={() => handleBulkAction('restart')}>
                        Restart Resources
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>

                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="destructive" size="sm">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Resources</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete {selectedResources.length} selected resource(s)? 
                        This action cannot be undone.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction 
                        onClick={() => handleBulkAction('delete')}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Delete Resources
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          )}

          {/* Resources Table */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-12">
                    <Checkbox
                      checked={selectedResources.length === filteredResources.length && filteredResources.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Resource</TableHead>
                  <TableHead>Provider</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Health</TableHead>
                  <TableHead>Region</TableHead>
                  <TableHead>Cost/Month</TableHead>
                  <TableHead>Security</TableHead>
                  <TableHead className="w-12">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredResources.map((resource) => {
                  const TypeIcon = typeIcons[resource.type];
                  const isSelected = selectedResources.includes(resource.id);
                  
                  return (
                    <TableRow key={resource.id}>
                      <TableCell>
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={(checked) => 
                            handleSelectResource(resource.id, checked as boolean)
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <TypeIcon className="w-5 h-5 text-muted-foreground" />
                          <div>
                            <p className="font-medium text-foreground">{resource.name}</p>
                            <p className="text-sm text-muted-foreground">{resource.instance_type}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={providerColors[resource.provider]}>
                          {resource.provider.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell className="capitalize">{resource.type}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[resource.status]}>
                          {resource.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${healthColors[resource.health]}`} />
                          <span className="text-sm capitalize">{resource.health}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{resource.region}</TableCell>
                      <TableCell className="font-medium">${resource.cost.toFixed(2)}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Shield className="w-4 h-4 text-muted-foreground" />
                          <span className="text-sm">{resource.security_score}/100</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem>
                              <Settings className="w-4 h-4 mr-2" />
                              Configure
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Activity className="w-4 h-4 mr-2" />
                              Monitor
                            </DropdownMenuItem>
                            <DropdownMenuItem>
                              <Shield className="w-4 h-4 mr-2" />
                              Security Scan
                            </DropdownMenuItem>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem className="text-red-600">
                              <Trash2 className="w-4 h-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          {filteredResources.length === 0 && (
            <div className="text-center py-8">
              <Cloud className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">No resources found</h3>
              <p className="text-muted-foreground">
                {searchTerm || providerFilter !== "all" || typeFilter !== "all" 
                  ? "Try adjusting your filters to see more results."
                  : "Get started by creating your first resource."
                }
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}